import { useInfiniteQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { ListMissionsParams, MissionSummary } from "../types";
import type { VisibilityScope } from "@/components/agentic-v2/VisibilityFilter";
import type {
  MissionsCaseloadScope,
  MissionsFilters,
} from "@/components/missions/missionsFilters";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import type {
  MissionControlEventSubscription,
  V2MissionStageChangedPayload,
  V2TaskNeedsAttentionPayload,
  V2TaskStatusChangedPayload,
} from "@/context/MissionControlWebSocketContext";
import { missionControlKeys, type MissionsPageKeyInput } from "./keys";
import { MISSION_CONTROL_PAGE_SIZE } from "./feedLimits";
import { missionControlSearchQueryForApi } from "./missionControlSearch";
import {
  applyMissionStageChangedToMissionDetailCache,
  patchMissionFieldsInList,
  patchMissionInList,
} from "./patches";

/** Map MissionsFilterBar state to ``GET /missions`` query parameters. */
export function missionsFiltersToApiParams(
  filters: MissionsFilters,
  caseload?: MissionsCaseloadScope,
): ListMissionsParams {
  const apiParams: ListMissionsParams = {};
  if (filters.templateId !== "all") apiParams.template_id = filters.templateId;
  if (filters.statusFilter === "needs_attention") {
    apiParams.needs_attention = true;
  } else if (filters.statusFilter !== "all") {
    apiParams.status = filters.statusFilter;
  }
  if (caseload) {
    apiParams.visibility = caseload.visibility;
    if (caseload.asUserId) apiParams.as_user_id = caseload.asUserId;
  } else if (filters.assigned === "me") {
    apiParams.visibility = "me";
    apiParams.assigned = "me";
  } else if (filters.assigned === "team") {
    apiParams.visibility = "team";
  } else {
    apiParams.visibility = "all";
  }
  const q = missionControlSearchQueryForApi(filters.q);
  if (q) apiParams.q = q;
  return apiParams;
}

/** Serialise filter + caseload into a stable TanStack Query cache key fragment. */
export function buildMissionsFiltersKey(
  filters: MissionsFilters,
  caseload?: MissionsCaseloadScope,
): string {
  return JSON.stringify({
    t: filters.templateId,
    s: filters.statusFilter,
    a: caseload?.visibility ?? filters.assigned,
    as: caseload?.asUserId ?? null,
    q: missionControlSearchQueryForApi(filters.q).toLowerCase(),
  });
}

/** Build the full missions-page cache key input from filters and optional caseload. */
export function buildMissionsPageKeyInput(
  filters: MissionsFilters,
  caseload?: MissionsCaseloadScope,
): MissionsPageKeyInput {
  return {
    filtersKey: buildMissionsFiltersKey(filters, caseload),
    visibility: (caseload?.visibility ?? filters.assigned) as VisibilityScope,
    asUserId: caseload?.asUserId ?? null,
  };
}

/** WebSocket handlers that patch or invalidate the cached missions page. */
export function createMissionsPageWsSubscription(
  queryClient: QueryClient,
  missionsKeyInput: MissionsPageKeyInput,
): MissionControlEventSubscription {
  const patchMissionFromEvent = (
    missionId: string | null | undefined,
    mission?: MissionSummary | null,
    fallback?: Partial<MissionSummary>,
  ) => {
    if (!missionId) return;
    if (mission) {
      patchMissionInList(queryClient, missionsKeyInput, mission);
      return;
    }
    if (fallback && Object.keys(fallback).length > 0) {
      patchMissionFieldsInList(queryClient, missionsKeyInput, missionId, fallback);
    }
  };

  return {
    onMissionCreated: (payload: { mission_id: string; mission?: MissionSummary | null }) => {
      patchMissionFromEvent(payload.mission_id, payload.mission);
      if (!payload.mission) {
        void queryClient.invalidateQueries({
          queryKey: missionControlKeys.missionsPage(missionsKeyInput),
        });
      }
    },
    onMissionStageChanged: (payload: V2MissionStageChangedPayload) => {
      applyMissionStageChangedToMissionDetailCache(queryClient, payload);
      patchMissionFromEvent(payload.mission_id, payload.mission, {
        current_stage: payload.new_stage,
      });
    },
    onMissionCompleted: (payload: {
      mission_id: string;
      mission?: MissionSummary | null;
    }) => {
      patchMissionFromEvent(payload.mission_id, payload.mission, {
        status: "completed",
        progress_pct: 100,
      });
    },
    onTaskStatusChanged: (payload: V2TaskStatusChangedPayload) => {
      patchMissionFromEvent(payload.mission_id, payload.mission ?? null);
    },
    onTaskNeedsAttention: (payload: V2TaskNeedsAttentionPayload) => {
      patchMissionFromEvent(payload.mission_id, payload.mission ?? null);
    },
  };
}

export interface UseMissionsPageOptions {
  filters: MissionsFilters;
  caseload?: MissionsCaseloadScope;
  enabled?: boolean;
}

/**
 * Cached missions list for the Mission Control browser.
 * Pauses fetching when the OS window is minimised, unfocused, or in a background workspace.
 */
export function useMissionsPage({
  filters,
  caseload,
  enabled: enabledOption = true,
}: UseMissionsPageOptions) {
  const isWindowLive = useWindowLive();
  const queryEnabled = enabledOption && isWindowLive;
  const keyInput = useMemo(
    () => buildMissionsPageKeyInput(filters, caseload),
    [filters, caseload],
  );

  const query = useInfiniteQuery({
    queryKey: missionControlKeys.missionsPage(keyInput),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const apiParams = missionsFiltersToApiParams(filters, caseload);
      return AgenticV2API.listMissions({
        ...apiParams,
        limit: MISSION_CONTROL_PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      });
    },
    getNextPageParam: (last) =>
      last.has_more ? last.next_cursor ?? undefined : undefined,
  });

  const missions = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  return { ...query, missions };
}

/** QueryClient accessor for missions-page cache patches (WS events, toolbar refresh). */
export function useMissionsPageQueryClient() {
  return useQueryClient();
}
